import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/* ------------------------------------------------------------------ */
/* Payment profile (terms + per-category method + Stripe status)       */
/* ------------------------------------------------------------------ */

export type PaymentMethodValue = 'check' | 'card' | 'ach' | null;

export interface PaymentProfileResponse {
  ok: true;
  profile: {
    payment_terms: string | null;
    /** Sales-tax status. false = non-taxable (the norm here). */
    taxable: boolean;
    payment_method_orders: PaymentMethodValue;
    payment_method_rentals: PaymentMethodValue;
    payment_method_tanks: PaymentMethodValue;
  };
  stripe: {
    needed: boolean;
    has_stripe_customer: boolean;
    active_methods: number;
    methods: Array<{
      kind: 'card' | 'ach';
      brand: string | null;
      last4: string | null;
      is_default: boolean;
    }>;
  };
  /** Live from the QBO customer record (Terms + preferred payment method);
   *  null when the lookup was unavailable. Only present on GET. */
  qbo: { terms: string | null; payment_method: string | null } | null;
  billing_email: string | null;
  /**
   * What Service Fusion / QuickBooks said when the save was pushed out to
   * them. Empty = both agreed. The portal is the record either way — these are
   * things to fix downstream, not failed saves. Only present on POST.
   */
  push_notes?: string[];
}

export function usePaymentProfile(customerId: string) {
  return useQuery({
    queryKey: ['admin', 'payment-profile', customerId],
    queryFn: () =>
      adminFetch<PaymentProfileResponse>(
        `admin-payment-profile?customer_id=${encodeURIComponent(customerId)}`,
      ),
    staleTime: 30_000,
  });
}

export function useSavePaymentProfile(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      payment_terms?: string | null;
      taxable?: boolean;
      payment_method_orders?: PaymentMethodValue;
      payment_method_rentals?: PaymentMethodValue;
      payment_method_tanks?: PaymentMethodValue;
    }) =>
      adminFetch<PaymentProfileResponse>('admin-payment-profile', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, ...input }),
      }),
    // Invalidate rather than setQueryData: the POST response skips the live
    // QBO lookup (fast save), so a refetch keeps the QuickBooks row intact.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'payment-profile', customerId] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Customer documents (tax ID / resale certificate vault)              */
/* ------------------------------------------------------------------ */

export type CustomerDocType = 'tax_id' | 'resale_cert' | 'other';

export interface CustomerDocument {
  id: string;
  customer_id: string;
  doc_type: CustomerDocType;
  label: string | null;
  doc_number: string | null;
  file_path: string | null;
  file_name: string | null;
  file_url: string | null;
  expires_on: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  archived_at: string | null;
}

export function useCustomerDocuments(customerId: string, archived = false) {
  return useQuery({
    queryKey: ['admin', 'customer-documents', customerId, archived ? 'archived' : 'active'],
    queryFn: () =>
      adminFetch<{ ok: true; documents: CustomerDocument[] }>(
        `admin-customer-documents?customer_id=${encodeURIComponent(customerId)}${
          archived ? '&archived=1' : ''
        }`,
      ),
    staleTime: 30_000,
  });
}

export function useUploadCustomerDocument(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      doc_type: CustomerDocType;
      doc_number?: string | null;
      expires_on?: string | null;
      notes?: string | null;
      file?: { name: string; data: string } | null;
    }) =>
      adminFetch<{ ok: true }>('admin-customer-documents', {
        method: 'POST',
        body: JSON.stringify({ action: 'upload', customer_id: customerId, ...input }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'customer-documents', customerId] }),
  });
}

export function useArchiveCustomerDocument(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ ok: true }>('admin-customer-documents', {
        method: 'POST',
        body: JSON.stringify({ action: 'archive', id }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'customer-documents', customerId] }),
  });
}

/* ------------------------------------------------------------------ */
/* Account-info request emails                                         */
/* ------------------------------------------------------------------ */

export function useRequestAccountInfo(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | { kind: 'payment_info'; note?: string | null }
        | { kind: 'documents'; doc_types: Array<'tax_id' | 'resale_cert'>; note?: string | null },
    ) =>
      adminFetch<{ ok: true; sent_to: string }>('admin-request-account-info', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, ...input }),
      }),
    onSuccess: () =>
      // The comms history panel shows the send immediately.
      qc.invalidateQueries({ queryKey: ['admin', 'activity-log'] }),
  });
}

/** FileReader → data-URL, for the document upload input. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}
